"use client";
import { useState, useMemo, useEffect } from 'react';
import Image from "next/image";
import Link from "next/link";
import styles from "./blog-grid.module.css";

import { useLocale, useTranslations } from 'next-intl';

export default function BlogGrid({ initialPosts }) {
    const locale = useLocale();
    const t = useTranslations('Filters');
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortOrder, setSortOrder] = useState('newest'); // 'newest' or 'oldest'

    // ... (rest of logic remains same until return) ...

    const itemsPerPage = 6;

    // Helper for localization
    const getLocalized = (post, key) => {
        if (post[key + '_i18n'] && post[key + '_i18n'][locale]) {
            return post[key + '_i18n'][locale];
        }
        return post[key];
    };

    // Filter and Sort Logic
    const filteredPosts = useMemo(() => {
        let posts = [...initialPosts];

        // Search
        if (searchTerm) {
            const lowerTerm = searchTerm.toLowerCase();
            posts = posts.filter(post => {
                const title = getLocalized(post, 'title').toLowerCase();
                const excerpt = (getLocalized(post, 'excerpt') || '').toLowerCase();
                return title.includes(lowerTerm) || excerpt.includes(lowerTerm);
            });
        }

        // Sort
        posts.sort((a, b) => {
            const dateA = new Date(a.date || a.published_at);
            const dateB = new Date(b.date || b.published_at);
            return sortOrder === 'newest' ? dateB - dateA : dateA - dateB;
        });

        return posts;
    }, [initialPosts, searchTerm, sortOrder, locale]);

    // Pagination logic
    const totalPages = Math.ceil(filteredPosts.length / itemsPerPage);
    const paginatedPosts = useMemo(() => {
        const start = (currentPage - 1) * itemsPerPage;
        return filteredPosts.slice(start, start + itemsPerPage);
    }, [currentPage, filteredPosts, itemsPerPage]);

    // Reset pagination when filter changes
    // useMemo should not have side effects. Use useEffect instead.
    const { useEffect } = require('react');
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, sortOrder]);

    return (
        <div>
            {/* Controls */}
            <div className={styles.controls}>
                <input
                    type="text"
                    placeholder={t('searchArticles')}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className={styles.searchInput}
                />
                <select
                    value={sortOrder}
                    onChange={(e) => setSortOrder(e.target.value)}
                    className={styles.sortSelect}
                >
                    <option value="newest">{t('newestFirst')}</option>
                    <option value="oldest">{t('oldestFirst')}</option>
                </select>
            </div>

            {/* Grid */}
            <div className={styles.grid}>
                {paginatedPosts.length > 0 ? (
                    paginatedPosts.map((post, index) => (
                        <div key={post.id || index} className={styles.card}>
                            <div className={styles.imageWrapper}>
                                <Image
                                    src={post.image}
                                    alt={getLocalized(post, 'title')}
                                    fill
                                    className={styles.image}
                                />
                            </div>
                            <div className={styles.content}>
                                <span className={styles.date}>
                                    {new Date(post.date || post.published_at).toLocaleDateString(locale === 'ar' ? 'ar-SA' : 'en-US', {
                                        year: 'numeric',
                                        month: 'long',
                                        day: 'numeric'
                                    })}
                                </span>
                                <h3 className={styles.title}>{getLocalized(post, 'title')}</h3>
                                <p className={styles.excerpt}>{getLocalized(post, 'excerpt')}</p>
                                <div className={styles.cardFooter}>
                                    <Link href={`/${locale}/blog/${post.seo?.slug || post.id}`} className={styles.readMore}>
                                        {t('readMore')} &rarr;
                                    </Link>
                                </div>
                            </div>
                        </div>
                    ))
                ) : (
                    <div className={styles.noResults}>
                        <p>{t('noArticles')}</p>
                        <button onClick={() => { setSearchTerm(''); setSortOrder('newest'); }} className={styles.clearButton}>
                            {t('clearFilters')}
                        </button>
                    </div>
                )}
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
                <div className={styles.pagination}>
                    <button
                        disabled={currentPage === 1}
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        className={styles.pageButton}
                    >
                        &lt; {t('prev')}
                    </button>
                    <span>{t('page')} {currentPage} of {totalPages}</span>
                    <button
                        disabled={currentPage === totalPages}
                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                        className={styles.pageButton}
                    >
                        {t('next')} &gt;
                    </button>
                </div>
            )}
        </div>
    );
}
